import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Hash, MailCheck, Send } from 'lucide-react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';

import { AuthError, AuthField, AuthHeading, AuthSubmit, confirmEmailSchema, requireAuth, useAuth } from '@/app/auth';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';

export const Route = createFileRoute('/login/confirmar-email')({
  beforeLoad: requireAuth,
  component: ConfirmEmailScreen,
});

type Values = z.infer<ReturnType<typeof confirmEmailSchema>>;

/** Destino do alerta "Você não está verificado" da casca: envia e confirma o código de e-mail. */
function ConfirmEmailScreen() {
  const { t } = useLingui();
  const { session, pending, error, codeSent, user } = useAuth();
  const navigate = useNavigate();
  const form = useForm<Values>({ resolver: zodResolver(confirmEmailSchema()), defaultValues: { code: '' } });
  const email = user?.email;

  const onSubmit = form.handleSubmit(async ({ code }) => {
    const state = await session.confirmEmail(code);
    if (!state.error) await navigate({ href: session.consumeRedirect(), replace: true });
  });

  return (
    <form className="flex flex-col gap-7" noValidate onSubmit={(event) => void onSubmit(event)}>
      <AuthHeading icon={MailCheck} title={<Trans>Confirmação de e-mail</Trans>}>
        <Trans>Um código será enviado por e-mail. Digite no campo abaixo e clique em Acessar para confirmar sua conta.</Trans>
      </AuthHeading>
      <AuthError message={error} />
      <div className="flex flex-col gap-3">
        <Button variant="secondary" block size="lg" className="md:h-11" onClick={() => void session.sendEmailVerificationCode()}>
          <Send aria-hidden className="size-4" />
          {codeSent ? <Trans>Reenviar código</Trans> : <Trans>Pronto para receber o código? Clique aqui</Trans>}
        </Button>
        {codeSent ? (
          <Alert tone="ok" className="animate-fade-in">
            {email ? <Trans>Código enviado para {email}. Confira sua caixa de entrada.</Trans> : <Trans>Código enviado. Confira seu e-mail.</Trans>}
          </Alert>
        ) : null}
      </div>
      <AuthField
        label={t`Código de confirmação`}
        icon={Hash}
        code
        inputMode="numeric"
        autoComplete="one-time-code"
        registration={form.register('code')}
        error={form.formState.errors.code}
      />
      <AuthSubmit pending={pending}>
        <Trans>Acessar</Trans>
      </AuthSubmit>
    </form>
  );
}
